'use client';

import * as React from 'react';

import { useRender } from '@base-ui/react/use-render';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
} from 'react-hook-form';

import { Trans } from '../ci/trans';

import { Label } from '#components/label';
import { cn } from '#utils';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const EMPTY_FIELD_STATE = {
  invalid: false,
  isDirty: false,
  isTouched: false,
  isValidating: false,
  error: undefined,
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();

  // `Form*` components are also rendered bare — outside a <FormField> — on
  // many screens (label + control wired up manually). The context default is
  // `{}`, so `name` is undefined there; degrade to an empty field state
  // instead of throwing (the id/aria wiring below still works from
  // FormItemContext). Do NOT tighten this to a throw: it crashes every bare
  // usage at render.
  const fieldName = fieldContext?.name;

  // Explicit subscription — RHF's `formState` Proxy getters are invisible to
  // the React Compiler, so reading `formState` from `useFormContext()` alone
  // can leave FormMessage/FormLabel stuck on a stale empty error (PAY-2015:
  // reserved team-name Zod errors never appeared under the field). Bare
  // usages have no field to watch, so their subscription is disabled.
  const formState = useFormState(
    fieldName ? { name: fieldName } : { disabled: true },
  );

  const fieldState = fieldName
    ? getFieldState(fieldName, formState)
    : EMPTY_FIELD_STATE;

  const { id } = itemContext;

  return {
    id,
    name: fieldName,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem: React.FC<React.ComponentPropsWithRef<'div'>> = ({
  className,
  ...props
}) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={cn('flex flex-col gap-y-2', className)} {...props} />
    </FormItemContext.Provider>
  );
};
FormItem.displayName = 'FormItem';

const FormLabel: React.FC<React.ComponentPropsWithRef<typeof Label>> = ({
  className,
  ...props
}) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
};
FormLabel.displayName = 'FormLabel';

const FormControl: React.FC<
  React.PropsWithChildren & {
    className?: string;
    render?: React.ReactElement;
  }
> = ({ render, children, className, ...props }) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  // Mirror the Radix `Slot` behaviour the app relied on before Base UI: merge
  // the control's id/aria wiring onto the single child element, so
  // `<FormControl><Input {...field} /></FormControl>` puts `id={formItemId}` on
  // the input itself (not a wrapper div). That keeps `FormLabel`'s `htmlFor`
  // association — and `getByLabel(...)` in e2e — pointing at the actual field.
  // Call sites already migrated to the explicit `render` prop still win.
  const renderElement =
    render ?? (React.isValidElement(children) ? children : undefined);

  return useRender({
    defaultTagName: 'div',
    render: renderElement,
    props: {
      ...props,
      id: formItemId,
      'aria-describedby': !error
        ? `${formDescriptionId}`
        : `${formDescriptionId} ${formMessageId}`,
      'aria-invalid': !!error,
      className: cn(className),
      // A render element carries its own children; only supply children when
      // falling back to the wrapper div.
      ...(renderElement ? {} : { children }),
    },
  });
};
FormControl.displayName = 'FormControl';

const FormDescription: React.FC<React.ComponentPropsWithRef<'p'>> = ({
  className,
  ...props
}) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      id={formDescriptionId}
      className={cn('text-muted-foreground text-[0.8rem]', className)}
      {...props}
    />
  );
};
FormDescription.displayName = 'FormDescription';

const FormMessage: React.FC<React.ComponentPropsWithRef<'p'>> = ({
  className,
  children,
  ...props
}) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : children;

  if (!body) {
    return null;
  }

  return (
    <p
      id={formMessageId}
      className={cn('text-destructive text-[0.8rem] font-medium', className)}
      {...props}
    >
      {/* Zod schemas use i18n keys as messages (e.g. teams.reservedNameError);
          translate them, falling back to the raw string for plain messages. */}
      {typeof body === 'string' ? (
        <Trans i18nKey={body} defaults={body} />
      ) : (
        body
      )}
    </p>
  );
};
FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
